import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, message, Space, Modal } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import api from '../utils/api';

const AccessRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/access-requests');
      setRequests(response.data);
    } catch (error) {
      message.error('Failed to fetch access requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (status) => {
    try {
      setActionLoading(true);
      await api.put(`/api/access-requests/${selectedRequest.id}`, { status });
      message.success(`Access request ${status}`);
      fetchRequests();
      setModalVisible(false);
    } catch (error) {
      message.error(`Failed to ${status} request`);
    } finally {
      setActionLoading(false);
    }
  };

  const showActionModal = (record, action) => {
    setSelectedRequest(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: 'Duration (days)',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'default';
        switch (status) {
          case 'approved':
            color = 'success';
            break;
          case 'rejected':
            color = 'error';
            break;
          case 'pending':
            color = 'processing';
            break;
          default:
            color = 'default';
        }
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        if (record.status === 'pending') {
          return (
            <Space>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => showActionModal(record, 'approve')}
                size="small"
              >
                Approve
              </Button>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => showActionModal(record, 'reject')}
                size="small"
              >
                Reject
              </Button>
            </Space>
          );
        }
        return (
          <Tag color={record.status === 'approved' ? 'success' : 'error'}>
            {record.status === 'approved' ? 'Approved' : 'Rejected'} by {record.resolved_by}
          </Tag>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Access Requests</h2>
      </div>

      <Table
        columns={columns}
        dataSource={requests}
        rowKey="id"
        loading={loading}
        scroll={{ x: true }}
      />

      <Modal
        title="Confirm Action"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="approve"
            type="primary"
            loading={actionLoading}
            onClick={() => handleAction('approved')}
          >
            Approve
          </Button>,
          <Button
            key="reject"
            danger
            loading={actionLoading}
            onClick={() => handleAction('rejected')}
          >
            Reject
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to take action on the access request from{' '}
          <strong>{selectedRequest?.user}</strong> for the{' '}
          <strong>{selectedRequest?.department}</strong> department?
        </p>
        <p>
          <strong>Reason:</strong> {selectedRequest?.reason}
        </p>
        <p>
          <strong>Duration:</strong> {selectedRequest?.duration} days
        </p>
      </Modal>
    </div>
  );
};

export default AccessRequests; 